<template>
  <MRow :gutter="28">
    <MCol>
      <MRow :gutter="0">
        <MCol>
          <h6 class="text-primary-alt font-semibold">{{ $t('other_info') }}</h6>
        </MCol>
      </MRow>
      <MRow :key="qrKey">
        <MCol>
          <QrCode class="mt-3" :asset-id="value.id" :module-name="moduleName" />
        </MCol>
      </MRow>
      <MRow
        v-if="
          moduleName !== $constants.ASSET_SOFTWARE &&
          moduleName !== $constants.ASSET_CONSUMABLE
        "
        class="my-3"
      >
        <MCol>
          <div>
            <small class="sidebar-field-lable">
              {{ $tc('used_by') }}
            </small>
          </div>
          <div class="bordered-bottom">
            <FlotoRequesterPicker
              :value="value.assignedUserIds"
              input-classes="material-input"
              multiple
              :class="{
                'read-only': disabled,
              }"
              :disabled="disabled"
              :placeholder="disabled ? '---' : undefined"
              :trigger-tag-style="{ maxWidth: '175px' }"
              as-input
              @change="handleChange({ assignedUserIds: $event })"
            />
          </div>
        </MCol>
      </MRow>
      <slot name="before"></slot>
      <MRow
        v-if="
          moduleName === $constants.ASSET_SOFTWARE &&
          (!allowedFields || allowedFields.indexOf('version') >= 0)
        "
        class="my-3"
      >
        <MCol>
          <div>
            <small class="sidebar-field-lable">
              {{ $tc('version') }}
            </small>
          </div>
          <div>
            <Version
              :disabled="disabled"
              :value="value.version"
              :can-delete="false"
              @change="handleChange({ version: $event })"
            />
          </div>
        </MCol>
      </MRow>
      <MRow
        v-if="
          moduleName !== $constants.ASSET_SOFTWARE &&
          (!allowedFields || allowedFields.indexOf('locationId') >= 0)
        "
        class="my-3"
      >
        <MCol>
          <div>
            <small class="sidebar-field-lable">
              {{ $tc('location') }}
            </small>
          </div>
          <div>
            <FlotoLocationPicker
              :value="value.locationId"
              :class="{ 'read-only': disabled }"
              :disabled="disabled"
              :placeholder="disabled ? '---' : undefined"
              input-classes="material-input"
              :module-name="moduleName"
              full-path
              @change="handleChange({ locationId: $event })"
            />
          </div>
        </MCol>
      </MRow>
      <MRow
        v-if="
          moduleName === $constants.ASSET_SOFTWARE &&
          (!allowedFields || allowedFields.indexOf('swCategoryId') >= 0)
        "
        class="my-3"
      >
        <MCol>
          <div>
            <small class="sidebar-field-lable">
              {{ $tc('software') }} {{ $tc('category') }}
            </small>
          </div>
          <div>
            <FlotoCategoryPicker
              :value="value.swCategoryId"
              :class="{
                'read-only material-input': disabled,
                'material-input': !disabled,
              }"
              :disabled="disabled"
              :placeholder="disabled ? '---' : undefined"
              class="w-full"
              :module-name="$constants.ASSET_SOFTWARE"
              @change="handleChange({ swCategoryId: $event })"
            />
          </div>
        </MCol>
      </MRow>
      <MRow class="my-3">
        <MCol>
          <div>
            <small class="text-neutral-light">
              {{ $tc('department') }}
            </small>
          </div>
          <div>
            <FlotoDepartmentPicker
              :class="{ 'read-only': disabled }"
              :placeholder="disabled ? '---' : undefined"
              :value="value.assignedDepartmentId"
              :disabled="disabled"
              class="material-input w-full"
              :module-name="moduleName"
              as-input
              full-path
              @change="handleChange({ assignedDepartmentId: $event })"
            />
          </div>
        </MCol>
      </MRow>
      <MRow v-if="enableMsp" class="my-3">
        <MCol>
          <div>
            <small class="text-neutral-light">
              {{ $tc('company') }}
            </small>
          </div>
          <div>
            <CompanyPicker
              :class="{ 'read-only': disabled }"
              :placeholder="disabled ? '---' : undefined"
              :value="value.companyId"
              :disabled="disabled"
              class="material-input w-full"
              as-input
              @change="handleChange({ companyId: $event })"
            />
          </div>
        </MCol>
      </MRow>
      <MRow
        v-if="
          [$constants.ASSET_HARDWARE, $constants.CMDB].indexOf(moduleName) >=
            0 &&
          (!allowedFields || allowedFields.indexOf('hostName') >= 0)
        "
        class="my-3"
      >
        <MCol>
          <div>
            <small class="sidebar-field-lable">
              {{ $tc('host_name') }}
            </small>
          </div>
          <div>
            <HostName
              :rules="{
                required: false,
                // eslint-disable-next-line
                short_text: true,
              }"
              :disabled="disabled"
              :value="value.hostName"
              :can-delete="false"
              @change="handleChange({ hostName: $event })"
            />
          </div>
        </MCol>
      </MRow>
      <MRow
        v-if="
          [$constants.ASSET_HARDWARE, $constants.CMDB].indexOf(moduleName) >=
            0 &&
          (!allowedFields || allowedFields.indexOf('domainName') >= 0)
        "
        class="my-3"
      >
        <MCol>
          <div>
            <small class="sidebar-field-lable">
              {{ $tc('domain_name') }}
            </small>
          </div>
          <div>
            <DomainName
              :disabled="disabled"
              :value="value.domainName"
              :can-delete="false"
              @change="handleChange({ domainName: $event })"
            />
          </div>
        </MCol>
      </MRow>
      <MRow
        v-if="
          [$constants.ASSET_HARDWARE, $constants.CMDB].indexOf(moduleName) >=
            0 &&
          (!allowedFields || allowedFields.indexOf('computerUuid') >= 0)
        "
        class="my-3"
      >
        <MCol>
          <div>
            <small class="sidebar-field-lable">
              {{ $tc('computerUuid') }}
            </small>
          </div>
          <div>
            <HostName
              :field="$tc('computerUuid')"
              :rules="{
                required: false,
                // eslint-disable-next-line
                short_text: true,
              }"
              :disabled="disabled"
              :value="value.computerUuid"
              :can-delete="false"
              @change="handleChange({ computerUuid: $event })"
            />
          </div>
        </MCol>
      </MRow>
      <MRow
        v-if="
          [$constants.ASSET_HARDWARE, $constants.CMDB].indexOf(moduleName) >=
            0 &&
          (!allowedFields || allowedFields.indexOf('ipAddress') >= 0)
        "
        class="my-3"
      >
        <MCol>
          <div>
            <small class="sidebar-field-lable">
              {{ $tc('ip_address') }}
            </small>
          </div>
          <div>
            <IPAddress
              :rules="{ required: false }"
              :disabled="disabled"
              :value="value.ipAddress"
              :can-delete="false"
              @change="handleChange({ ipAddress: $event })"
            />
          </div>
        </MCol>
      </MRow>
      <MRow
        v-if="
          [$constants.ASSET_HARDWARE, $constants.CMDB].indexOf(moduleName) >=
            0 &&
          (!allowedFields || allowedFields.indexOf('macAddress') >= 0)
        "
        class="my-3"
      >
        <MCol>
          <div>
            <small class="sidebar-field-lable">
              {{ $tc('mac_address') }}
            </small>
          </div>
          <div>
            <MacAddress
              :rules="{ required: false }"
              :disabled="disabled"
              :value="value.macAddress"
              :can-delete="false"
              @change="handleChange({ macAddress: $event })"
            />
          </div>
        </MCol>
      </MRow>
      <MRow
        v-if="
          [$constants.ASSET_HARDWARE, $constants.CMDB].indexOf(moduleName) >=
            0 &&
          (!allowedFields || allowedFields.indexOf('subnetMask') >= 0)
        "
        class="my-3"
      >
        <MCol>
          <div>
            <small class="sidebar-field-lable">
              {{ $tc('subnet_mask') }}
            </small>
          </div>
          <div>
            <IPAddress
              :rules="{ required: false }"
              :disabled="disabled"
              :label="$tc('subnet_mask')"
              :value="value.subnetMask"
              :can-delete="false"
              @change="handleChange({ subnetMask: $event })"
            />
          </div>
        </MCol>
      </MRow>
      <MRow
        v-if="
          moduleName !== $constants.ASSET_CONSUMABLE &&
          (!allowedFields || allowedFields.indexOf('vendorId') >= 0)
        "
        class="my-3"
      >
        <MCol>
          <div>
            <small class="sidebar-field-lable">
              {{ $tc('vendor') }}
            </small>
          </div>
          <div>
            <VendorPicker
              :value="value.vendorId"
              :class="{ 'read-only': disabled }"
              :disabled="disabled"
              :placeholder="disabled ? '---' : undefined"
              class="material-input w-full"
              as-input
              @change="handleChange({ vendorId: $event })"
            />
          </div>
        </MCol>
      </MRow>
      <MRow
        v-if="
          [
            $constants.ASSET_SOFTWARE,
            $constants.ASSET_CONSUMABLE,
            $constants.CMDB,
          ].indexOf(moduleName) === -1 &&
          (!allowedFields || allowedFields.indexOf('assetCondition') >= 0)
        "
        class="my-3"
      >
        <MCol>
          <div>
            <small class="sidebar-field-lable">
              {{ $tc('asset') }} {{ $tc('condition') }}
            </small>
          </div>
          <div>
            <AssetConditionPicker
              :value="value.assetCondition"
              :class="{ 'read-only': disabled }"
              :disabled="disabled || value.movementStatus !== 'none'"
              :placeholder="disabled ? '---' : undefined"
              class="material-input w-full"
              as-input
              :allow-clear="false"
              @change="handleChange({ assetCondition: $event })"
            />
          </div>
        </MCol>
      </MRow>
      <MRow
        v-if="
          [
            $constants.ASSET_SOFTWARE,
            $constants.ASSET_CONSUMABLE,
            $constants.CMDB,
          ].indexOf(moduleName) === -1 &&
          (!allowedFields || allowedFields.indexOf('movementStatus') >= 0)
        "
        class="my-3"
      >
        <MCol>
          <div>
            <small class="sidebar-field-lable">
              {{ $tc('movement') }} {{ $tc('status') }}
            </small>
          </div>
          <div>
            <AssetMovementStatusPicker
              :value="value.movementStatus"
              disabled
              placeholder="---"
              class="material-input w-full read-only"
              as-input
              :allow-clear="false"
              @change="handleChange({ movementStatus: $event })"
            />
          </div>
        </MCol>
      </MRow>
      <MRow
        v-if="
          [$constants.ASSET_CONSUMABLE, $constants.CMDB].indexOf(moduleName) ===
            -1 &&
          (!allowedFields || allowedFields.indexOf('underChangeControl') >= 0)
        "
        class="my-3 mb-0"
      >
        <MCol>
          <div>
            <small class="sidebar-field-lable">
              {{ $tc('under_change_control') }}
            </small>
          </div>
          <div class="py-1">
            {{ value.underChangeControl === true ? 'Yes' : 'No' }}
          </div>
        </MCol>
      </MRow>
      <MRow v-if="moduleName !== $constants.ASSET_CONSUMABLE" class="my-3">
        <MCol>
          <div>
            <small class="sidebar-field-lable">
              {{ $t('business') }} {{ $tc('service') }}
            </small>
          </div>
          <div>
            <FlotoBusinessServicePicker
              :value="value.businessServiceId"
              input-classes="material-input"
              :class="{ 'read-only': disabled }"
              :disabled="disabled"
              :placeholder="disabled ? '---' : undefined"
              as-input
              @change="handleChange({ businessServiceId: $event })"
            />
          </div>
        </MCol>
      </MRow>
      <MRow
        v-if="!allowedFields || allowedFields.indexOf('origin') >= 0"
        class="my-3"
      >
        <MCol>
          <div>
            <small class="sidebar-field-lable">
              {{ $tc('origin') }}
            </small>
          </div>
          <div>
            <OriginPicker
              :value="value.origin"
              input-classes="material-input"
              :class="{ 'read-only': true }"
              disabled
              :placeholder="disabled ? '---' : undefined"
              as-input
            />
          </div>
        </MCol>
      </MRow>
      <MRow
        v-if="
          value.resourceId &&
          (!allowedFields || allowedFields.indexOf('resourceId') >= 0)
        "
        class="my-3"
      >
        <MCol>
          <div>
            <small class="sidebar-field-lable">
              {{ __tc('resource_id') }}
            </small>
          </div>
          <div>
            {{ value.resourceId }}
          </div>
        </MCol>
      </MRow>
      <MRow
        v-if="
          [$constants.ASSET_CONSUMABLE, $constants.CMDB].indexOf(moduleName) ===
            -1 &&
          (!allowedFields || allowedFields.indexOf('acquitionTime') >= 0)
        "
        class="my-3"
      >
        <MCol>
          <div>
            <small class="sidebar-field-lable">
              {{ $t('acquisition_date') }}
            </small>
          </div>
          <div>
            <FlotoDatePicker
              class="material-input w-full"
              :disabled="disabled"
              :value="value.acquitionTime"
              :show-time="false"
              @change="handleChange({ acquitionTime: $event })"
            />
          </div>
        </MCol>
      </MRow>
      <MRow
        v-if="
          [$constants.ASSET_SOFTWARE, $constants.ASSET_CONSUMABLE].indexOf(
            moduleName
          ) === -1 &&
          (!allowedFields || allowedFields.indexOf('assignmentTime') >= 0)
        "
        class="my-3"
      >
        <MCol>
          <div>
            <small class="sidebar-field-lable">
              {{ $t('assignment_date') }}
            </small>
          </div>
          <div>
            <FlotoDatePicker
              class="material-input w-full"
              :disabled="disabled"
              :value="value.assignmentTime"
              :show-time="false"
              @change="handleChange({ assignmentTime: $event })"
            />
          </div>
        </MCol>
      </MRow>
      <MRow
        v-if="
          [
            $constants.ASSET_SOFTWARE,
            $constants.ASSET_CONSUMABLE,
            $constants.CMDB,
          ].indexOf(moduleName) === -1
        "
        class="my-3"
      >
        <MCol>
          <div>
            <small class="sidebar-field-lable">
              {{ $tc('last_barcode_qrcode_scan_by') }}
            </small>
          </div>
          <div class="bordered-bottom">
            <FlotoRequesterPicker
              :value="value.lastQRAndBarcodeScanBy"
              input-classes="material-input"
              disabled
              text-only
              :placeholder="'---'"
              :trigger-tag-style="{ maxWidth: '65%' }"
              as-input
            />
          </div>
        </MCol>
      </MRow>
      <MRow
        v-if="
          [
            $constants.ASSET_SOFTWARE,
            $constants.ASSET_CONSUMABLE,
            $constants.CMDB,
          ].indexOf(moduleName) === -1
        "
        class="my-3"
      >
        <MCol>
          <div>
            <small class="sidebar-field-lable">
              {{ $tc('last_barcode_qrcode_scan_date') }}
            </small>
          </div>
          <div>
            {{ value.lastQRAndBarcodeScanDate | datetime }}
          </div>
          <div v-if="value.lastQRAndBarcodeScanDate">
            ({{ value.lastQRAndBarcodeScanDate | timeago }})
          </div>
          <div v-if="!value.lastQRAndBarcodeScanDate">---</div>
        </MCol>
      </MRow>
      <MRow v-if="value.createdAt" class="my-3">
        <MCol>
          <div>
            <small class="sidebar-field-lable">
              {{ $t('created_time') }}
            </small>
          </div>
          <div>
            {{ value.createdAt | datetime }}
          </div>
          <div> ({{ value.createdAt | timeago }}) </div>
        </MCol>
      </MRow>
      <MRow class="my-3">
        <MCol>
          <div>
            <small class="sidebar-field-lable">
              {{ $t('last_updated_date') }}
            </small>
          </div>
          <div>
            {{ value.updatedAt | datetime }}
          </div>
          <div> ({{ value.updatedAt | timeago }}) </div>
        </MCol>
      </MRow>
    </MCol>
  </MRow>
</template>

<script>
import IPAddress from '@components/data-picker/ip-address'
import MacAddress from '@components/data-picker/mac-address'
import HostName from '@components/data-picker/host-name'
import DomainName from '@components/data-picker/domain-name'
import Version from '@components/data-picker/version'
import VendorPicker from '@components/data-picker/vendor-picker'
import AssetConditionPicker from '@components/data-picker/asset-condition-picker'
import AssetMovementStatusPicker from '@components/data-picker/asset-movement-status-picker'
import OriginPicker from '@components/data-picker/origin-picker'
import { stageConstants } from '@utils/status'
import { authComputed } from '@state/modules/auth'
import QrCode from '../qr-code.vue'
import { MspConfigComputed } from '@state/modules/msp-config/helpers'
import CompanyPicker from '@components/data-picker/company-picker'

export default {
  name: 'Sidebar',
  components: {
    VendorPicker,
    IPAddress,
    MacAddress,
    HostName,
    DomainName,
    Version,
    OriginPicker,
    QrCode,
    AssetConditionPicker,
    AssetMovementStatusPicker,
    CompanyPicker,
  },
  model: {
    event: 'change',
  },
  props: {
    value: { type: Object, required: true },
    moduleName: { type: String, required: true },
    disabled: { type: Boolean, default: false },
    allowedFields: { type: Array, default: undefined },
  },
  data() {
    this.stageConstants = stageConstants
    return {
      qrKey: 1,
    }
  },
  computed: {
    ...authComputed,
    ...MspConfigComputed,
  },
  watch: {
    value() {
      this.qrKey++
    },
  },
  methods: {
    handleChange(change) {
      this.$emit('change', change)
    },
  },
}
</script>

<style lang="less" scoped>
.bordered-bottom {
  @apply border-solid border-neutral-lighter border-b border-t-0 border-l-0 border-r-0;
}
</style>
